import React, { useState, useEffect } from 'react';
import { X, Trash2, Save, AlertTriangle } from 'lucide-react';
import { GlassButton } from '../ui';
import { JourneyEdge, JourneyEdgeTrigger, JourneyNode, getSequence, getLeadForms } from '../../services/api';
import { CrmLeadStagePicker } from './CrmLeadStagePicker';

interface Props {
    edge: JourneyEdge;
    allNodes: JourneyNode[];
    projectId: string;
    onSave: (edge: JourneyEdge) => void;
    onDelete: (edgeId: string) => void;
    onClose: () => void;
}

interface StepOption { step_id: string; step_order: number; subject_line: string | null; }
interface FormOption  { form_id: string; name: string; }

const TRIGGER_TYPES: { value: JourneyEdgeTrigger['type']; label: string }[] = [
    { value: 'campaign_start',    label: 'Campaign starts' },
    { value: 'email_opened',      label: 'Email opened' },
    { value: 'link_clicked',      label: 'Link clicked' },
    { value: 'no_open',           label: 'No open after N days' },
    { value: 'no_click',          label: 'No click after N days' },
    { value: 'sequence_completed', label: 'Sequence completed' },
    { value: 'lead_form',         label: 'Lead form submitted' },
    { value: 'crm_lead_stage',    label: 'CRM lead stage changed' },
];

const NEEDS_SOURCE   = new Set(['email_opened', 'link_clicked', 'no_open', 'no_click', 'sequence_completed']);
const NEEDS_DAYS     = new Set(['email_opened', 'link_clicked', 'no_open', 'no_click']);
const NEEDS_STEP     = new Set(['email_opened', 'link_clicked', 'no_open', 'no_click']);

export default function JourneyEdgePanel({ edge, allNodes, projectId, onSave, onDelete, onClose }: Props) {
    const [draft, setDraft] = useState<JourneyEdge>({ ...edge, trigger: { ...edge.trigger } });
    const [steps, setSteps]       = useState<StepOption[]>([]);
    const [forms, setForms]       = useState<FormOption[]>([]);
    const [loadingSteps, setLoadingSteps] = useState(false);
    const [loadingForms, setLoadingForms] = useState(false);

    // Sync draft when a different edge is opened
    useEffect(() => {
        setDraft({ ...edge, trigger: { ...edge.trigger } });
        setSteps([]);
    }, [edge.id]);

    // Fetch steps when source sequence changes (for per-email triggers)
    useEffect(() => {
        const seqId = draft.trigger.source_sequence_id;
        if (!seqId || !NEEDS_STEP.has(draft.trigger.type)) {
            setSteps([]);
            return;
        }
        setLoadingSteps(true);
        getSequence(seqId)
            .then(seq => setSteps(
                seq.steps
                    .filter((s: any) => s.step_type === 'email')
                    .map((s: any) => ({ step_id: s.step_id, step_order: s.step_order, subject_line: s.subject_line }))
                    .sort((a: StepOption, b: StepOption) => a.step_order - b.step_order)
            ))
            .catch(() => setSteps([]))
            .finally(() => setLoadingSteps(false));
    }, [draft.trigger.source_sequence_id, draft.trigger.type]);

    // Fetch lead forms for the project when trigger type is lead_form
    useEffect(() => {
        if (draft.trigger.type !== 'lead_form') {
            setForms([]);
            return;
        }
        setLoadingForms(true);
        getLeadForms(projectId)
            .then(data => setForms(data.map((f: any) => ({ form_id: f.form_id, name: f.name }))))
            .catch(() => setForms([]))
            .finally(() => setLoadingForms(false));
    }, [draft.trigger.type, projectId]);

    const fromNode = allNodes.find(n => n.id === draft.from_node);
    const toNode   = allNodes.find(n => n.id === draft.to_node);

    const fromName = draft.from_node === '__entry__'
        ? 'Campaign Start'
        : (fromNode?.edm_sequence?.name ?? fromNode?.id ?? '?');
    const toName = toNode?.edm_sequence?.name ?? (toNode as any)?.name ?? toNode?.id ?? '?';

    const ttype      = draft.trigger.type;
    const needsSource = NEEDS_SOURCE.has(ttype);
    const needsDays   = NEEDS_DAYS.has(ttype);
    const needsStep   = NEEDS_STEP.has(ttype) && !!draft.trigger.source_sequence_id;

    const updateTrigger = (patch: Partial<JourneyEdgeTrigger>) => {
        setDraft(d => ({ ...d, trigger: { ...d.trigger, ...patch } }));
    };

    const handleSave  = () => onSave(draft);
    const handleDelete = () => {
        if (window.confirm('Delete this trigger edge?')) onDelete(draft.id);
    };

    return (
        <div className="journey-edge-panel">
            <div className="journey-edge-panel-header">
                <button
                    onClick={onClose}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, alignSelf: 'flex-start' }}
                    title="Close"
                >
                    <X size={16} />
                </button>
                <div>
                    <div className="journey-edge-panel-title">{fromName} → {toName}</div>
                    <div className="journey-edge-panel-subtitle">Trigger edge</div>
                </div>
            </div>

            <div className="journey-edge-panel-body">
                {/* ── Trigger section ── */}
                <div className="journey-edge-panel-section">
                    <div className="journey-edge-panel-section-label">Trigger</div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {/* Trigger type */}
                        <select
                            className="journey-select"
                            value={ttype}
                            onChange={e => updateTrigger({
                                type: e.target.value as JourneyEdgeTrigger['type'],
                                source_sequence_id: undefined,
                                step_id: undefined,
                                step_label: undefined,
                                after_days: undefined,
                                form_id: undefined,
                                form_name: undefined,
                                stage: undefined,
                            })}
                        >
                            {TRIGGER_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>

                        {/* Source sequence picker */}
                        {needsSource && (
                            <select
                                className="journey-select"
                                value={draft.trigger.source_sequence_id ?? ''}
                                onChange={e => updateTrigger({
                                    source_sequence_id: e.target.value || undefined,
                                    step_id: undefined,
                                    step_label: undefined,
                                })}
                            >
                                <option value="">— in sequence —</option>
                                {allNodes
                                    .filter(n => !(n as any).type || (n as any).type === 'sequence')
                                    .map(n => (
                                        <option key={n.id} value={n.edm_sequence_id}>
                                            {n.edm_sequence?.name ?? n.edm_sequence_id}
                                        </option>
                                    ))}
                            </select>
                        )}

                        {/* Per-email step picker */}
                        {needsStep && (
                            <select
                                className="journey-select"
                                value={draft.trigger.step_id ?? ''}
                                disabled={loadingSteps}
                                onChange={e => {
                                    const chosen = steps.find(s => s.step_id === e.target.value);
                                    updateTrigger({
                                        step_id: e.target.value || undefined,
                                        step_label: chosen
                                            ? `Email ${chosen.step_order + 1}${chosen.subject_line ? ': ' + chosen.subject_line : ''}`
                                            : undefined,
                                    });
                                }}
                            >
                                <option value="">— any email in sequence —</option>
                                {steps.map(s => (
                                    <option key={s.step_id} value={s.step_id}>
                                        Email {s.step_order + 1}{s.subject_line ? `: ${s.subject_line}` : ''}
                                    </option>
                                ))}
                            </select>
                        )}

                        {/* After N days */}
                        {needsDays && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>after</span>
                                <input
                                    type="number"
                                    className="journey-input"
                                    style={{ width: 72 }}
                                    min={0}
                                    max={365}
                                    value={draft.trigger.after_days ?? 0}
                                    onChange={e => updateTrigger({ after_days: Number(e.target.value) })}
                                />
                                <span style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>days</span>
                            </div>
                        )}

                        {/* Lead form picker */}
                        {ttype === 'lead_form' && (
                            <select
                                className="journey-select"
                                value={draft.trigger.form_id ?? ''}
                                disabled={loadingForms}
                                onChange={e => {
                                    const chosen = forms.find(f => f.form_id === e.target.value);
                                    updateTrigger({
                                        form_id: e.target.value || undefined,
                                        form_name: chosen?.name,
                                    });
                                }}
                            >
                                <option value="">
                                    {loadingForms ? 'Loading forms…' : forms.length === 0 ? 'No forms in this project' : '— any form —'}
                                </option>
                                {forms.map(f => (
                                    <option key={f.form_id} value={f.form_id}>{f.name}</option>
                                ))}
                            </select>
                        )}

                        {/* CRM stage */}
                        {ttype === 'crm_lead_stage' && (
                            <CrmLeadStagePicker
                                value={draft.trigger.stage ?? ''}
                                onChange={v => updateTrigger({ stage: v })}
                            />
                        )}
                    </div>
                </div>

                {/* ── Behaviour section ── */}
                <div className="journey-edge-panel-section">
                    <div className="journey-edge-panel-section-label">Behaviour</div>

                    <label className="journey-checkbox-row">
                        <input
                            type="checkbox"
                            checked={draft.halt_source}
                            onChange={e => {
                                const halt = e.target.checked;
                                setDraft(d => ({
                                    ...d,
                                    halt_source: halt,
                                    resume_on_complete: halt ? d.resume_on_complete : false,
                                }));
                            }}
                        />
                        Halt originating sequence
                    </label>

                    <label className={`journey-checkbox-row${!draft.halt_source ? ' journey-checkbox-row--disabled' : ''}`}>
                        <input
                            type="checkbox"
                            checked={draft.resume_on_complete}
                            disabled={!draft.halt_source}
                            onChange={e => setDraft(d => ({ ...d, resume_on_complete: e.target.checked }))}
                        />
                        Resume when complete
                    </label>

                    {!draft.halt_source && (
                        <div className="journey-flow-warning">
                            <AlertTriangle size={13} style={{ display: 'inline', marginRight: 5 }} />
                            Both sequences will send to this recipient concurrently. The journey send cap applies.
                        </div>
                    )}
                </div>
            </div>

            <div className="journey-edge-panel-footer">
                <GlassButton variant="primary" onClick={handleSave}>
                    <Save size={14} />
                    Save
                </GlassButton>
                <GlassButton variant="secondary" onClick={handleDelete} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                    <Trash2 size={14} />
                    Delete
                </GlassButton>
            </div>
        </div>
    );
}
